import {
  ApolloClient,
  ApolloLink,
  gql,
  HttpLink,
  InMemoryCache,
  Reference,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'

import i18n from '@app/i18n'

import { BRAND_ID } from '@shared/contants'
import { ProductPriceOffer } from '@shared/generated'
import { sentryErrorLink } from '@shared/lib/sentry-apollo'

import 'regenerator-runtime/runtime'

const API = import.meta.env.VITE_API

console.log('API URL:', API)
const typeDefs = gql`
  extend type Product {
    priceOffer: ProductPriceOffer
  }
`

const priceOfferFragment = gql`
  fragment priceOfferFragment on ProductPriceOffer {
    id
    price
    oldPrice
    count_multiplier
    salePeriod {
      start
      finish
    }
    min
    max
    step
    quant
    uiQuant
    quantAmount
    isDefault
    group
  }
`

const GET_PRICE_GROUP = gql`
  query GetPriceGroup {
    localPriceGroup @client
  }
`

const getAmountInQuantType = (quantType: string) => {
  switch (quantType) {
  case 'KGM':
    return 1000
  default:
    return 1
  }
}

const getCountMultilpierAmount = (quant: string, quantAmount: number) => {
  const amountInQuantType = getAmountInQuantType(quant)
  return amountInQuantType * (quantAmount > 0 ? quantAmount : 1)
}

const cache = new InMemoryCache({
  typePolicies: {
    ProductPriceOffer: {
      fields: {
        count_multiplier: {
          read (_, { readField }) {
            const quant: string = readField('quant') ?? 'defaultQuant'
            const quantAmount: number = readField('quantAmount') ?? 1
            return getCountMultilpierAmount(quant, quantAmount)
          },
        },
      },
    },
    Product: {
      fields: {
        priceOffer: {
          read (_, { readField, cache }) {
            const priceOffersRefs: readonly Reference[] | undefined = readField(
              {
                fieldName: 'priceOffers',
              },
            )
            const priceOffers: ProductPriceOffer[] =
              priceOffersRefs
                ?.map((ref) =>
                  cache.readFragment({
                    fragment: priceOfferFragment,
                    id: cache.identify(ref),
                  }),
                )
                .filter(
                  (offer): offer is ProductPriceOffer => offer !== null,
                ) ?? []

            const price_group = cache.readQuery<{ localPriceGroup?: string }>({
              query: GET_PRICE_GROUP,
            })?.localPriceGroup

            const selected =
              priceOffers.find((offer) =>
                price_group
                  ? offer.group === price_group
                  : offer.isDefault === true,
              ) ??
              priceOffers[0]

            if (!selected) return null

            // `priceOffer` is declared as `PriceOffer` in the schema, while the
            // source objects are `ProductPriceOffer`. Re-tag the offer (and its
            // nested salePeriod) so the cache can read the selection set back.
            return {
              ...selected,
              __typename: 'PriceOffer',
              salePeriod: selected.salePeriod
                ? { ...selected.salePeriod, __typename: 'SalePeriod' }
                : null,
            }
          },
        },
      },
    },
  },
})

const langLink = setContext((_, { headers }) => ({
  headers: {
    ...headers,
    'x-hive-brand': BRAND_ID,
    'Accept-Language': i18n.language || 'kk',
  },
}))

const httpLink = new HttpLink({
  uri: API,
  credentials: 'include',
})

const cleanTypeName = new ApolloLink((operation, forward) => {
  if (operation.variables) {
    const omitTypename = (key: string, value: unknown) =>
      key === '__typename' ? undefined : value
    operation.variables = JSON.parse(
      JSON.stringify(operation.variables),
      omitTypename,
    )
  }
  return forward(operation)
})

export const client = new ApolloClient({
  cache,
  link: ApolloLink.from([ sentryErrorLink, cleanTypeName, langLink, httpLink ]),
  typeDefs,
})
